import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query'
import { 
  Plus, Check, X, Edit2, Trash2, Calendar, User, 
  ChevronRight, ChevronDown, Flag, Circle, MoreVertical, GripVertical 
} from 'lucide-react'
import { taskAPI, widgetAPI } from '../../services/api'
import { useModalClose } from '../../hooks/useModalClose'
import toast from 'react-hot-toast'
import { format } from 'date-fns'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

interface TaskWidgetProps {
  widget: any
  isExpanded: boolean
}

interface Task {
  id: number
  title: string
  description?: string
  status: 'todo' | 'in_progress' | 'completed' | 'cancelled'
  priority: 'low' | 'medium' | 'high' | 'urgent'
  color: string
  due_date?: string
  assigned_to?: number
  assigned_to_username?: string
  subtasks?: Task[]
  parent_task_id?: number
}

export default function TaskWidget({ widget, isExpanded }: TaskWidgetProps) {
  const [showAddTask, setShowAddTask] = useState(false)
  const [editingTask, setEditingTask] = useState<Task | null>(null)
  const [expandedTasks, setExpandedTasks] = useState<Set<number>>(new Set())
  const [selectedTask, setSelectedTask] = useState<Task | null>(null)
  const [localTasks, setLocalTasks] = useState<Task[]>([])
  const [hideCompleted, setHideCompleted] = useState(widget.config?.hideCompleted ?? false)
  const queryClient = useQueryClient()

  const { data: tasks = [], isLoading } = useQuery({
    queryKey: ['tasks'],
    queryFn: async () => {
      const { data } = await taskAPI.getAll()
      setLocalTasks(data)
      return data
    },
  })

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const createMutation = useMutation({
    mutationFn: (taskData: any) => taskAPI.create(taskData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] })
      toast.success('Task created!')
      setShowAddTask(false)
    },
    onError: () => toast.error('Failed to create task'),
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => taskAPI.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] })
      toast.success('Task updated!')
      setEditingTask(null)
      setSelectedTask(null)
    },
    onError: () => toast.error('Failed to update task'),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => taskAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] })
      toast.success('Task deleted!')
      setSelectedTask(null)
    },
    onError: () => toast.error('Failed to delete task'),
  })

  const completeMutation = useMutation({
    mutationFn: (id: number) => taskAPI.complete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] })
    },
  })

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      const oldIndex = localTasks.findIndex((task) => task.id === active.id)
      const newIndex = localTasks.findIndex((task) => task.id === over.id)

      const newTasks = arrayMove(localTasks, oldIndex, newIndex)
      setLocalTasks(newTasks)

      // Update positions in backend
      try {
        await Promise.all(
          newTasks.map((task, index) =>
            taskAPI.update(task.id, { position: index })
          )
        )
      } catch (error) {
        toast.error('Failed to save order')
        queryClient.invalidateQueries({ queryKey: ['tasks'] })
      }
    }
  }

  const toggleTaskExpanded = (taskId: number) => {
    const newExpanded = new Set(expandedTasks)
    if (newExpanded.has(taskId)) {
      newExpanded.delete(taskId)
    } else {
      newExpanded.add(taskId)
    }
    setExpandedTasks(newExpanded)
  }

  const handleToggleHideCompleted = async (value: boolean) => {
    setHideCompleted(value)
    try {
      const newConfig = {
        ...widget.config,
        hideCompleted: value,
      }
      await widgetAPI.update(widget.id, {
        config_data: JSON.stringify(newConfig)
      })
      // Update local widget config
      widget.config = newConfig
    } catch (error) {
      console.error('Failed to save hide completed preference:', error)
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'text-red-600'
      case 'high': return 'text-orange-600'
      case 'medium': return 'text-yellow-600'
      case 'low': return 'text-green-600'
      default: return 'text-gray-600'
    }
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800'
      case 'in_progress': return 'bg-blue-100 text-blue-800'
      case 'cancelled': return 'bg-gray-100 text-gray-800'
      default: return 'bg-gray-50 text-gray-600'
    }
  }

  const colorOptions = [
    '#3B82F6', // Blue
    '#10B981', // Green
    '#F59E0B', // Orange
    '#EF4444', // Red
    '#8B5CF6', // Purple
    '#EC4899', // Pink
    '#14B8A6', // Teal
    '#6B7280', // Gray
  ]

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-gray-500">Loading tasks...</div>
      </div>
    )
  }

  // Filter tasks based on hideCompleted setting
  const filteredTasks = hideCompleted 
    ? localTasks.filter(task => task.status !== 'completed')
    : localTasks

  return (
    <div className="h-full flex flex-col p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>Tasks</h3>
        <div className="flex items-center gap-3">
          {/* Hide Completed Toggle */}
          <label className="flex items-center gap-2 cursor-pointer text-sm" style={{ color: 'var(--color-text-secondary)' }}>
            <span>Hide completed</span>
            <div className="relative">
              <input
                type="checkbox"
                checked={hideCompleted}
                onChange={(e) => handleToggleHideCompleted(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary-600"></div>
            </div>
          </label>

          <button
            onClick={() => setShowAddTask(true)}
            className="flex items-center gap-1 px-3 py-1 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition text-sm"
          >
            <Plus size={16} />
            Add Task
          </button>
        </div>
      </div>

      {/* Tasks List */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <div className="flex-1 overflow-y-auto space-y-2">
          <SortableContext
            items={filteredTasks.map((task) => task.id)}
            strategy={verticalListSortingStrategy}
          >
            {filteredTasks.map((task: Task) => (
              <SortableTaskItem
                key={task.id}
                task={task}
                level={0}
                isExpanded={expandedTasks.has(task.id)}
                onToggleExpand={() => toggleTaskExpanded(task.id)}
                onComplete={() => completeMutation.mutate(task.id)}
                onEdit={() => setEditingTask(task)}
                onDelete={() => {
                  if (confirm('Delete this task?')) {
                    deleteMutation.mutate(task.id)
                  }
                }}
                onClick={() => setSelectedTask(task)}
                colorOptions={colorOptions}
                getPriorityColor={getPriorityColor}
                getStatusBadge={getStatusBadge}
              />
            ))}

            {filteredTasks.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <p>
                  {hideCompleted && localTasks.length > 0 
                    ? 'All tasks completed! 🎉' 
                    : 'No tasks yet. Create one to get started!'}
                </p>
              </div>
            )}
          </SortableContext>
        </div>
      </DndContext>

      {/* Add Task Modal */}
      {showAddTask && createPortal(
        <TaskFormModal
          title="New Task"
          onClose={() => setShowAddTask(false)}
          onSubmit={(data) => createMutation.mutate(data)}
          colorOptions={colorOptions}
        />,
        document.body
      )}

      {/* Edit Task Modal */}
      {editingTask && createPortal(
        <TaskFormModal
          title="Edit Task"
          task={editingTask}
          onClose={() => setEditingTask(null)}
          onSubmit={(data) => updateMutation.mutate({ id: editingTask.id, data })}
          colorOptions={colorOptions}
        />,
        document.body
      )}

      {/* Task Details Modal */}
      {selectedTask && !editingTask && createPortal(
        <TaskDetailsModal
          task={selectedTask}
          onClose={() => setSelectedTask(null)}
          onEdit={() => {
            setEditingTask(selectedTask)
            setSelectedTask(null)
          }}
          onDelete={() => {
            if (confirm('Delete this task?')) {
              deleteMutation.mutate(selectedTask.id)
            }
          }}
          onUpdate={(data) => updateMutation.mutate({ id: selectedTask.id, data })}
          colorOptions={colorOptions}
          getPriorityColor={getPriorityColor}
          getStatusBadge={getStatusBadge}
        />,
        document.body
      )}
    </div>
  )
}

// Sortable Task Item Wrapper
function SortableTaskItem({ task, ...props }: any) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: task.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <div ref={setNodeRef} style={style}>
      <TaskItem task={task} dragHandleProps={{ ...attributes, ...listeners }} {...props} />
    </div>
  )
}

// Task Item Component
function TaskItem({ task, level = 0, isExpanded, onToggleExpand, onComplete, onEdit, onDelete, onClick, colorOptions, getPriorityColor, getStatusBadge, dragHandleProps }: any) {
  const hasSubtasks = task.subtasks && task.subtasks.length > 0

  return (
    <div style={{ marginLeft: `${level * 20}px` }}>
      <div
        className="flex items-center gap-2 p-2 bg-white border border-gray-200 rounded-lg hover:shadow-sm transition cursor-pointer"
        onClick={(e) => {
          if (!(e.target as HTMLElement).closest('button')) {
            onClick()
          }
        }}
      >
        {/* Drag Handle */}
        {level === 0 && dragHandleProps && (
          <div
            {...dragHandleProps}
            className="cursor-grab active:cursor-grabbing p-1 hover:bg-gray-100 rounded"
            onClick={(e) => e.stopPropagation()}
          >
            <GripVertical size={16} className="text-gray-400" />
          </div>
        )}

        {/* Color Indicator */}
        <div
          className="w-1 h-8 rounded-full flex-shrink-0"
          style={{ backgroundColor: task.color }}
        />

        {/* Expand/Collapse for subtasks */}
        {hasSubtasks && (
          <button
            onClick={(e) => {
              e.stopPropagation()
              onToggleExpand()
            }}
            className="p-1 hover:bg-gray-100 rounded"
          >
            {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </button>
        )}

        {/* Checkbox */}
        <button
          onClick={(e) => {
            e.stopPropagation()
            onComplete()
          }}
          className={`flex-shrink-0 w-5 h-5 border-2 rounded flex items-center justify-center transition ${
            task.status === 'completed'
              ? 'bg-green-500 border-green-500'
              : 'border-gray-300 hover:border-green-500'
          }`}
        >
          {task.status === 'completed' && <Check size={14} className="text-white" />}
        </button>

        {/* Title */}
        <span
          className={`flex-1 text-sm ${
            task.status === 'completed' ? 'line-through text-gray-500' : 'text-gray-900'
          }`}
        >
          {task.title}
        </span>

        {/* Priority Flag */}
        <Flag size={14} className={getPriorityColor(task.priority)} />

        {/* Due Date */}
        {task.due_date && (
          <div className="flex items-center gap-1 text-xs text-gray-500">
            <Calendar size={12} />
            {format(new Date(task.due_date), 'MMM d')}
          </div>
        )}

        {/* Assigned To */}
        {task.assigned_to_username && (
          <div className="flex items-center gap-1 text-xs bg-gray-100 px-2 py-1 rounded">
            <User size={12} />
            {task.assigned_to_username}
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-1">
          <button
            onClick={(e) => {
              e.stopPropagation()
              onEdit()
            }}
            className="p-1 hover:bg-gray-100 rounded"
          >
            <Edit2 size={14} className="text-gray-600" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation()
              onDelete()
            }}
            className="p-1 hover:bg-red-50 rounded"
          >
            <Trash2 size={14} className="text-red-600" />
          </button>
        </div>
      </div>

      {/* Subtasks */}
      {isExpanded && hasSubtasks && (
        <div className="mt-1">
          {task.subtasks.map((subtask: Task) => (
            <TaskItem
              key={subtask.id}
              task={subtask}
              level={level + 1}
              isExpanded={false}
              onToggleExpand={() => {}}
              onComplete={onComplete}
              onEdit={onEdit}
              onDelete={onDelete}
              onClick={onClick}
              colorOptions={colorOptions}
              getPriorityColor={getPriorityColor}
              getStatusBadge={getStatusBadge}
            />
          ))}
        </div>
      )}
    </div>
  )
}

// Task Form Modal
function TaskFormModal({ title, task, onClose, onSubmit, colorOptions }: any) {
  const { handleBackdropClick } = useModalClose(onClose)
  const [formData, setFormData] = useState({
    title: task?.title || '',
    description: task?.description || '',
    priority: task?.priority || 'medium',
    status: task?.status || 'todo',
    color: task?.color || '#3B82F6',
    due_date: task?.due_date ? task.due_date.split('T')[0] : '',
    assigned_to: task?.assigned_to || '',
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSubmit({
      ...formData,
      due_date: formData.due_date || null,
      assigned_to: formData.assigned_to || null,
    })
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]" onClick={handleBackdropClick}>
      <div className="bg-white rounded-xl shadow-2xl p-6 max-w-3xl w-full m-4 h-[85vh] overflow-y-auto flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
              autoFocus
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows={3}
            />
          </div>

          {/* Color */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Color
            </label>
            <div className="flex gap-2">
              {colorOptions.map((color: string) => (
                <button
                  key={color}
                  type="button"
                  onClick={() => setFormData({ ...formData, color })}
                  className={`w-8 h-8 rounded-full border-2 transition ${
                    formData.color === color ? 'border-gray-900 scale-110' : 'border-transparent'
                  }`}
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
          </div>

          {/* Priority & Status */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Priority
              </label>
              <select
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="todo">To Do</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>

          {/* Due Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Due Date
            </label>
            <input
              type="date"
              value={formData.due_date}
              onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Assigned To - Would need user list from API */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Assign To (User ID)
            </label>
            <input
              type="number"
              value={formData.assigned_to}
              onChange={(e) => setFormData({ ...formData, assigned_to: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter user ID"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-4">
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition"
            >
              Save
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// Task Details Modal
function TaskDetailsModal({ task, onClose, onEdit, onDelete, onUpdate, colorOptions, getPriorityColor, getStatusBadge }: any) {
  const [showColorPicker, setShowColorPicker] = useState(false)
  const { handleBackdropClick } = useModalClose(onClose)

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]" onClick={handleBackdropClick}>
      <div className="bg-white rounded-xl shadow-2xl p-6 max-w-4xl w-full m-4 h-[85vh] overflow-y-auto flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <div
                className="w-3 h-3 rounded-full cursor-pointer"
                style={{ backgroundColor: task.color }}
                onClick={() => setShowColorPicker(!showColorPicker)}
              />
              <h2 className="text-xl font-semibold text-gray-900">{task.title}</h2>
            </div>
            
            {showColorPicker && (
              <div className="flex gap-2 mb-3">
                {colorOptions.map((color: string) => (
                  <button
                    key={color}
                    onClick={() => {
                      onUpdate({ color })
                      setShowColorPicker(false)
                    }}
                    className="w-6 h-6 rounded-full border-2 border-gray-200 hover:border-gray-900"
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
            )}

            <div className="flex items-center gap-2 flex-wrap">
              <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusBadge(task.status)}`}>
                {task.status.replace('_', ' ')}
              </span>
              <span className={`flex items-center gap-1 text-sm ${getPriorityColor(task.priority)}`}>
                <Flag size={14} />
                {task.priority}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={onEdit}
              className="p-2 hover:bg-gray-100 rounded"
            >
              <Edit2 size={18} />
            </button>
            <button
              onClick={onDelete}
              className="p-2 hover:bg-red-50 rounded text-red-600"
            >
              <Trash2 size={18} />
            </button>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded">
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Details */}
        <div className="space-y-4">
          {/* Description */}
          {task.description && (
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-1">Description</h3>
              <p className="text-gray-600">{task.description}</p>
            </div>
          )}

          {/* Metadata */}
          <div className="grid grid-cols-2 gap-4">
            {/* Due Date */}
            {task.due_date && (
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-1">Due Date</h3>
                <div className="flex items-center gap-2 text-gray-600">
                  <Calendar size={16} />
                  {format(new Date(task.due_date), 'MMM d, yyyy')}
                </div>
              </div>
            )}

            {/* Assigned To */}
            {task.assigned_to_username && (
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-1">Assigned To</h3>
                <div className="flex items-center gap-2 text-gray-600">
                  <User size={16} />
                  {task.assigned_to_username}
                </div>
              </div>
            )}
          </div>

          {/* Subtasks */}
          {task.subtasks && task.subtasks.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">
                Subtasks ({task.subtasks.length})
              </h3>
              <div className="space-y-1">
                {task.subtasks.map((subtask: Task) => (
                  <div key={subtask.id} className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                    <Circle size={12} className="text-gray-400" />
                    <span className="flex-1 text-sm text-gray-900">{subtask.title}</span>
                    <span className={`px-2 py-0.5 rounded text-xs ${getStatusBadge(subtask.status)}`}>
                      {subtask.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Timestamps */}
          <div className="pt-4 border-t border-gray-200 text-xs text-gray-500">
            <div>Created: {format(new Date(task.created_at), 'MMM d, yyyy h:mm a')}</div>
            {task.completed_at && (
              <div>Completed: {format(new Date(task.completed_at), 'MMM d, yyyy h:mm a')}</div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

